import { useEffect } from "react";
import PAGE_META from "./pageMeta.json";

const SITE = "Sculpteur Français";
export const BASE = process.env.REACT_APP_SITE_URL;
const DEFAULT_IMAGE = `${BASE}/images/og-image.jpg`;

const CRUMBS = {
  "/definition-sculpteur-contemporain": "Définition du sculpteur contemporain",
  "/courants-et-mouvements": "Courants & mouvements",
  "/materiaux-et-techniques": "Matériaux & techniques",
  "/lexique-sculpture": "Lexique de la sculpture",
  "/guide-achat-direct-artiste": "Guide d'achat en direct",
  "/faq": "FAQ",
  "/contact": "Contact",
};

const MATERIAUX_LABELS = {
  bronze: "Le bronze",
  resine: "La résine",
  platre: "Le plâtre",
  metal: "Le métal",
  bois: "Le bois",
  pierre: "La pierre & le marbre",
};

// Même ordre que src/lib/partenaires.js (annuaire métier par métier)
const PARTENAIRES_LABELS = {
  ferronnerie: "Ferronnerie",
  resine: "Résine",
  "fonderie-art": "Fondeur d’art",
  finitions: "Finitions et décors de surface",
};

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setJsonLd(id, data) {
  let script = document.getElementById(id);
  if (data) {
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  } else if (script) {
    script.remove();
  }
}

function buildBreadcrumb(path, custom) {
  if (custom && custom.length) {
    const items = [{ "@type": "ListItem", position: 1, name: "Accueil", item: `${BASE}/` }];
    custom.forEach((c, i) => {
      items.push({ "@type": "ListItem", position: i + 2, name: c.name, item: `${BASE}${c.path}` });
    });
    return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
  }
  const items = [{ "@type": "ListItem", position: 1, name: "Accueil", item: `${BASE}/` }];
  if (CRUMBS[path]) {
    items.push({ "@type": "ListItem", position: 2, name: CRUMBS[path], item: `${BASE}${path}` });
  } else if (path.startsWith("/partenaires/")) {
    const [, , metierSlug] = path.split("/");
    items.push({ "@type": "ListItem", position: 2, name: "Partenaires", item: `${BASE}/partenaires-metiers-d-art` });
    items.push({
      "@type": "ListItem",
      position: 3,
      name: PARTENAIRES_LABELS[metierSlug] || metierSlug,
      item: `${BASE}/partenaires/${metierSlug}`,
    });
  } else if (path.startsWith("/materiaux-et-techniques/")) {
    const slug = path.split("/").pop();
    items.push({ "@type": "ListItem", position: 2, name: "Matériaux & techniques", item: `${BASE}/materiaux-et-techniques` });
    items.push({ "@type": "ListItem", position: 3, name: MATERIAUX_LABELS[slug] || slug, item: `${BASE}${path}` });
  } else {
    return null;
  }
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
}

export function useSEO(title, description, jsonLd = null, options = {}) {
  const json = jsonLd ? JSON.stringify(jsonLd) : null;
  const ogType = options.type || "website";
  const noindex = Boolean(options.noindex);
  const breadcrumbKey = options.breadcrumb ? JSON.stringify(options.breadcrumb) : "";
  // Le CSV pageMeta.json est la source de vérité : il prime sur les littéraux des pages
  const routeMeta = PAGE_META[window.location.pathname] || null;
  const finalTitle = (routeMeta && routeMeta.title) || title;
  const finalDesc = (routeMeta && routeMeta.description) || description;
  const ogImage = options.image || (routeMeta && routeMeta.ogImage) || DEFAULT_IMAGE;
  const ogTitle = options.ogTitle || (routeMeta && routeMeta.ogTitle) || finalTitle;
  const ogDesc = options.ogDescription || (routeMeta && routeMeta.ogDescription) || finalDesc;

  useEffect(() => {
    const path = window.location.pathname;
    const url = `${BASE}${path}`;
    document.title = finalTitle;
    document.documentElement.lang = "fr";

    setMeta("name", "description", finalDesc);
    setMeta("property", "og:title", ogTitle);
    setMeta("property", "og:description", ogDesc);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", SITE);
    setMeta("property", "og:locale", "fr_FR");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", ogTitle);
    setMeta("name", "twitter:description", ogDesc);
    setMeta("name", "twitter:image", ogImage);

    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", options.robots || "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    setJsonLd("page-jsonld", json ? JSON.parse(json) : null);
    setJsonLd("page-breadcrumb", buildBreadcrumb(path, options.breadcrumb));

    return () => {
      setJsonLd("page-jsonld", null);
      setJsonLd("page-breadcrumb", null);
    };
  }, [finalTitle, finalDesc, json, ogType, ogImage, ogTitle, ogDesc, noindex, breadcrumbKey]);
}
